import React, { useEffect, useMemo, useState } from "react";
import { FaCheck, FaTrash } from "react-icons/fa";
import "../assets/css/ServicePicker.css";

/** Extra wording shown in a service's panel heading (by category code). */
const HEADING_SUFFIX = { GD: "Branding" };

const panelHeading = (category) => {
  const suffix = HEADING_SUFFIX[String(category.category_code || "").toUpperCase()];
  return suffix ? `${category.category_name} (${suffix})` : category.category_name;
};

/**
 * Pick services (categories) first, then sub-services for each selected service.
 *
 * ``value`` is the list of selected sub-service ids (numbers for catalogue items,
 * string ids for custom services); ``onChange`` receives the next list.
 */
function ServicePicker({ categories, value, onChange, customServices = {}, onDeleteCustom }) {
  const selected = useMemo(() => value || [], [value]);

  const categoryOfService = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((c) => {
      (c.subcategories || []).forEach((s) => map.set(s.subcategory_id, c.category_id));
    });
    Object.entries(customServices).forEach(([id, s]) => map.set(id, s.categoryId));
    return map;
  }, [categories, customServices]);

  const [openIds, setOpenIds] = useState([]);

  // Services that already have sub-services selected (e.g. when editing) start open.
  useEffect(() => {
    const withSelection = new Set();
    selected.forEach((id) => {
      const cid = categoryOfService.get(id);
      if (cid != null) withSelection.add(cid);
    });
    if (withSelection.size === 0) return;
    setOpenIds((prev) => {
      const next = new Set(prev);
      withSelection.forEach((cid) => next.add(cid));
      return next.size === prev.length ? prev : Array.from(next);
    });
  }, [selected, categoryOfService]);

  const toggleCategory = (categoryId) => {
    if (openIds.includes(categoryId)) {
      setOpenIds((prev) => prev.filter((id) => id !== categoryId));
      onChange(selected.filter((id) => categoryOfService.get(id) !== categoryId));
    } else {
      setOpenIds((prev) => [...prev, categoryId]);
    }
  };

  const toggleService = (serviceId) => {
    onChange(
      selected.includes(serviceId)
        ? selected.filter((id) => id !== serviceId)
        : [...selected, serviceId]
    );
  };

  const openCategories = (categories || []).filter((c) => openIds.includes(c.category_id));

  return (
    <div className="service-picker">
      <p className="service-picker-label">Select one or more services</p>
      <div className="service-picker-chips" role="group" aria-label="Services">
        {(categories || []).map((c) => {
          const active = openIds.includes(c.category_id);
          const count = selected.filter((id) => categoryOfService.get(id) === c.category_id).length;
          return (
            <button
              key={c.category_id}
              type="button"
              className={`service-picker-chip${active ? " is-active" : ""}`}
              aria-pressed={active}
              onClick={() => toggleCategory(c.category_id)}
            >
              {active && <FaCheck size={10} aria-hidden="true" />}
              <span>{c.category_name}</span>
              {count > 0 && <span className="service-picker-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {openCategories.length > 0 && (
        <div className="services-card service-picker-panels">
          {openCategories.map((category) => {
            const subs = category.subcategories || [];
            const customForCategory = Object.entries(customServices)
              .filter(([, s]) => s.categoryId === category.category_id)
              .map(([id, s]) => ({ id, name: s.name }));
            const hasSelection = selected.some((id) => categoryOfService.get(id) === category.category_id);
            return (
              <div key={category.category_id} className="service-category">
                <h3 className="category-title">{panelHeading(category)}</h3>
                <div className="services-grid">
                  {subs.map((sub) => (
                    <label key={sub.subcategory_id} className="service-checkbox" title={sub.description || undefined}>
                      <input
                        type="checkbox"
                        checked={selected.includes(sub.subcategory_id)}
                        onChange={() => toggleService(sub.subcategory_id)}
                      />
                      <span>{sub.subcategory_name}</span>
                    </label>
                  ))}
                  {customForCategory.map(({ id, name }) => (
                    <div key={id} className="addlead-custom-service-row">
                      <label className="service-checkbox">
                        <input
                          type="checkbox"
                          checked={selected.includes(id)}
                          onChange={() => toggleService(id)}
                        />
                        <span>{name}</span>
                      </label>
                      {onDeleteCustom && (
                        <button
                          type="button"
                          className="addlead-custom-service-delete"
                          onClick={() => onDeleteCustom(id)}
                          title="Remove"
                        >
                          <FaTrash size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {!hasSelection && (
                  <p className="service-picker-empty">Choose at least one {category.category_name} sub-service.</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ServicePicker;
